import { useCallback, useEffect, useState } from 'react';
import cx from 'classnames';
import Button from '@ingka/button';
import Tooltip from '@ingka/tooltip';
import { useDispatch } from 'react-redux';
import CircleHalf from '@ingka/ssr-icon/paths/circle-half';
import styles from './ToggleThemeButton.module.scss';
import { trackUIInteraction } from '../../../utils/analyticsEvents';
import { useLocalStorage } from '../../../hooks/useLocalStorage';
import { setTheme as setThemeAppState } from '../../../store/slices/app';
import type { AppThunkDispatch } from '../../../store';

const ToggleThemeButton = function ToggleThemeButton() {
  const [darkThemePersisted, setDarkThemePersisted] = useLocalStorage<boolean>(
    'rc-user-settings-dark-theme',
    false,
  );
  const darkTheme =
    darkThemePersisted === undefined
      ? window.matchMedia('(prefers-color-scheme: dark)').matches
      : darkThemePersisted;

  const dispatch = useDispatch<AppThunkDispatch>();
  const [theme, setTheme] = useState(darkTheme);

  const toggleTheme = useCallback(() => {
    setDarkThemePersisted(!theme);
    trackUIInteraction('toggle_theme', { theme: theme ? 'dark' : 'light' });
    setTheme(!theme);
  }, [theme, setDarkThemePersisted]);

  useEffect(() => {
    document.querySelector('body')?.classList.remove('dark', 'light');
    document.querySelector('body')?.classList.add(theme ? 'dark' : 'light');
    dispatch(setThemeAppState({ theme: theme ? 'dark' : 'light' }));
  }, [dispatch, theme]);

  return (
    <Tooltip
      describedById="toggle-theme-tooltip"
      position="leading"
      tooltipText="Toggle theme"
    >
      <Button
        className={cx(styles.theme)}
        onClick={toggleTheme}
        iconOnly
        size="small"
        aria-label="Toggle theme"
        ssrIcon={CircleHalf}
        type="tertiary"
      />
    </Tooltip>
  );
};

export default ToggleThemeButton;
